<script setup lang="ts">
const route = useRoute();

const { modalType, isModalOpen, authInitialTab, authComplianceMode } =
  storeToRefs(useLayoutStore());

const { closeModal, closeHelp } = useLayoutStore();
const { $containment } = useNuxtApp();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { setAuth } = useAuthStore();
const { userCanOnlyWithdraw } = useUserLimits();
const { updateWallet } = useWallet();
const { isMobile } = useLayout();
const { isLoading } = storeToRefs(useUserDataStore());
const { showModalImage } = useModalImage();

// Trava o scroll do fundo enquanto o modal (depósito, saque, etc.) está aberto.
useBodyScrollLock(isModalOpen);

// Quando o registro ativo é o CompleteRegulatory, o compliance vem de DENTRO
// dele — então o AuthComplianceInfo irmão não deve renderizar (evita duplicar).
// Espelha a cadeia de v-if/v-else-if do template de registro.
const isCompleteRegulatoryActive = computed<boolean>(
  () =>
    !isSingleStepRegisterEnabled.value &&
    !isMultiStepRegisterEnabled.value &&
    (isCompleteRegulatoryRegisterEnabled.value ||
      isRegulatorySimplifiedRegisterEnabled.value),
);

const {
  authFlowWithModal,
  authFlowModalWithIframe,
  isAuthV2FlowEnabled,
  isMultiStepRegisterEnabled,
  isSingleStepRegisterEnabled,
  isCompleteRegulatoryRegisterEnabled,
  isRegulatorySimplifiedRegisterEnabled,
} = useAuth();

const multiStepRef = ref<InstanceType<typeof MultiStep> | null>(null);

const showAuthBannerModal = computed<boolean>(() => {
  if (isMultiStepRegisterEnabled.value) {
    if (isMobile.value) {
      return multiStepRef.value?.currentStep === 0;
    }

    return true;
  }

  return true;
});

const isAuthFlowType = computed<boolean>(() => {
  return [
    'register',
    'login',
    'forgot-password',
    'recovery-password-options',
    'attempt-limit',
    'reset-password',
    'two-factor-auth',
  ].includes(modalType.value as string);
});

const isAuthFlowAndV2 = computed<boolean>(() => {
  return isAuthV2FlowEnabled.value && isAuthFlowType.value;
});

const authFlowIframeSrc = computed<string>(() => {
  const TIMESTAMP_NOW = +new Date() * 1;
  const { BUILD_UNIQUE_ID } = useRuntimeConfig();

  const tsBuildId = `${BUILD_UNIQUE_ID || 'ts'}-${TIMESTAMP_NOW}`;

  const loginPath = `/login?ts=${tsBuildId}${
    authInitialTab.value === 'register' ? '&tab=register' : ''
  }`;

  const paths: Record<string, string> = {
    login: loginPath,
    register: `/register?ts=${tsBuildId}`,
    forgotPassword: `/forgot-password?ts=${tsBuildId}`,
    twoFactorAuth: `/login/two-factor?ts=${tsBuildId}`,
  };

  return paths[modalType.value as keyof typeof paths] || '';
});

const customIframeHeight = computed<number>(() => {
  if (isAuthFlowAndV2.value) {
    return isMobile.value ? 450 : 750;
  }

  const iframeHeights: Record<string, number> = {
    login: 660,
    register: 800,
    forgotPassword: 450,
    twoFactorAuth: 660,
  };

  return iframeHeights[modalType.value] || 700;
});

const customPanelClass = computed<string>(() => {
  if (modalType.value === 'deposit') {
    return 'sm:max-w-[500px]';
  }

  return '';
});

const customModalWidth = computed<number>(() => {
  if (isAuthFlowAndV2.value) {
    if (isMobile.value) {
      return 450;
    }

    return showModalImage.value ? 940 : 500;
  }

  const modalWidths: Record<string, number> = {
    withdraw: 500,
    bonus: 500,
    'bonus-withdraw': 500,
    contracts: 460,
    help: 860,
  };

  return modalWidths[modalType.value] || 450;
});

const isCloseButtonHidden = computed<boolean>(() => {
  if (
    isAuthFlowType.value ||
    modalType.value === 'check-activity' ||
    modalType.value === 'contracts' ||
    modalType.value === 'help'
  ) {
    return true;
  }

  return isLoading.value;
});

const isModalPersistent = computed<boolean>(() => {
  return isAuthFlowModalWithIframe.value || modalType.value === 'contracts';
});

const isGamificationPage = computed(() => {
  return route.path.includes('vip');
});

function onCloseModalButtonClick() {
  if (modalType.value === 'help') {
    closeHelp();

    return;
  }

  if (isAuthenticated.value) {
    if (!isGamificationPage.value) {
      updateWallet();
    }
  }

  isModalOpen.value = false;
}

const isLoadingIframe = ref<boolean>(true);

const iframeElement = ref<HTMLIFrameElement | null>(null);

async function listenPostMessage(message: MessageEvent) {
  if (message.source !== iframeElement.value?.contentWindow) {
    return;
  }

  console.debug('🚀 ~ listenPostMessage:', message.data);

  if (message.data.message === 'loginSuccess') {
    // @ts-expect-error
    await setAuth({
      data: message.data.responseData,
      headers: message.data.responseHeaders,
    });

    return;
  }

  if (message.data === 'closeAlert') {
    showRegisterQuit.value = true;

    return;
  }

  if (message.data === 'closeModal') {
    if (!showRegisterQuit.value) {
      isLoadingIframe.value = true;
      closeModal();
    }
  }

  if (message.data === 'iframeLoading') {
    isLoadingIframe.value = true;
  }

  if (message.data === 'iframeReady') {
    setTimeout(() => {
      isLoadingIframe.value = false;
    }, 300);
  }
}

const validModalTypesUnlogged: ModalType[] = [
  'login',
  'forgot-password',
  'recovery-password-options',
  'attempt-limit',
  'two-factor-auth',
  'register-quit',
  'help',
];

const validModalTypesLogged: ModalType[] = [
  'help',
  'deposit',
  'withdraw',
  'bonus-withdraw',
  'bonus-rules',
  'verification',
  'check-activity',
  'contracts',
];

function validateModalType() {
  if (!isAuthenticated.value) {
    if (!authFlowWithModal.value) {
      closeModal();
    }

    if (!validModalTypesUnlogged.includes(modalType.value)) {
      closeModal();
    }
  } else {
    if (!validModalTypesLogged.includes(modalType.value)) {
      closeModal();
    }

    if (userCanOnlyWithdraw.value && modalType.value === 'deposit') {
      closeModal();
    }
  }
}

watch(
  () => isModalOpen.value,
  () => {
    validateModalType();
  },
);

const isAuthFlowModalWithIframe = computed<boolean>(() => {
  if (!authFlowModalWithIframe.value) {
    return false;
  }

  return (
    modalType.value === 'login' ||
    modalType.value === 'forgot-password' ||
    modalType.value === 'two-factor-auth'
  );
});

const showRegisterQuit = ref<boolean>(false);

const onCloseModalClick = () => {
  isLoadingIframe.value = true;
  showRegisterQuit.value = false;
  closeModal();
};

const onCloseRegisterQuit = () => {
  isLoadingIframe.value = true;
  showRegisterQuit.value = false;
  closeModal();
};

const onContinueRegisterQuit = () => {
  showRegisterQuit.value = false;
};

onMounted(() => {
  validateModalType();

  window.addEventListener('message', listenPostMessage, { passive: true });

  const timeoutId = setTimeout(() => {
    isLoadingIframe.value = false;
  }, 6000);

  onUnmounted(() => {
    isLoadingIframe.value = true;
    window.removeEventListener('message', listenPostMessage);
    clearTimeout(timeoutId);
  });
});
</script>

<template>
  <BaseModal
    v-model="isModalOpen"
    :custom-class-modal="$containment.isVerificationModalOpen ? '!z-[999]' : ''"
    :custom-class-wrapper="customPanelClass"
    :hide-close-button="isCloseButtonHidden"
    :persistent="isModalPersistent"
    :width="customModalWidth"
    @close="onCloseModalButtonClick"
  >
    <template v-if="isAuthFlowType">
      <div :class="$style.modalIFrameContainer">
        <BaseLoading
          class="!bg-auth-bg-primary !absolute"
          :icon-loader="true"
          :show="isLoadingIframe && isAuthFlowModalWithIframe"
        />

        <template v-if="showRegisterQuit">
          <AuthRegisterQuitNew
            @close="onCloseRegisterQuit"
            @continue="onContinueRegisterQuit"
          />
        </template>

        <template v-if="isAuthFlowModalWithIframe">
          <iframe
            ref="iframeElement"
            :class="$style.authFlowIframe"
            frameborder="0"
            scrolling="no"
            :src="authFlowIframeSrc"
            :style="{ height: customIframeHeight + 'px' }"
          />
        </template>

        <template v-else-if="modalType === 'login'">
          <AuthPageLayout @action-to-close="onCloseModalClick">
            <AuthLogin />
          </AuthPageLayout>
        </template>

        <template v-else-if="modalType === 'register'">
          <AuthPageLayout
            :show-banner="showAuthBannerModal"
            :show-logo="isAuthV2FlowEnabled ? false : true"
            @action-to-close="onCloseModalClick"
          >
            <AuthRegisterSingleStep v-if="isSingleStepRegisterEnabled" />
            <AuthRegisterMultiStep
              v-else-if="isMultiStepRegisterEnabled"
              ref="multiStepRef"
            />
            <AuthRegisterCompleteRegulatory
              v-else-if="
                isCompleteRegulatoryRegisterEnabled ||
                isRegulatorySimplifiedRegisterEnabled
              "
            />
            <AuthRegisterBasic v-else />
            <!-- CompleteRegulatory traz o compliance embutido; os demais tipos
                 (single/multi/basic) precisam do aviso aqui. -->
            <AuthComplianceInfo
              v-if="authComplianceMode && !isCompleteRegulatoryActive"
            />
          </AuthPageLayout>
        </template>

        <template v-else-if="modalType === 'forgot-password'">
          <AuthPageLayout @action-to-close="onCloseModalClick">
            <AuthForgotPassword />
          </AuthPageLayout>
        </template>

        <template v-else-if="modalType === 'recovery-password-options'">
          <AuthPageLayout @action-to-close="onCloseModalClick">
            <AuthPasswordRecoveryOptions />
          </AuthPageLayout>
        </template>

        <template v-else-if="modalType === 'attempt-limit'">
          <AuthPageLayout @action-to-close="onCloseModalClick">
            <AuthLoginAttemptLimit />
          </AuthPageLayout>
        </template>

        <template v-else-if="modalType === 'reset-password'">
          <AuthPageLayout @action-to-close="onCloseModalClick">
            <AuthPasswordResetForm />
          </AuthPageLayout>
        </template>

        <template v-else-if="modalType === 'two-factor-auth'">
          <AuthPageLayout @action-to-close="onCloseModalClick">
            <AuthTwoFactorAuth />
          </AuthPageLayout>
        </template>
      </div>
    </template>

    <template v-else-if="modalType === 'verification'">
      <AuthVerification />
    </template>

    <template v-else-if="modalType === 'check-activity'">
      <BaseModalCheckActivity />
    </template>

    <template v-else-if="modalType === 'contracts'">
      <BaseModalContracts />
    </template>

    <template v-else-if="modalType === 'help'">
      <BaseModalHelp />
    </template>

    <template v-else>
      <BaseModalImage />

      <div class="relative overflow-hidden">
        <template v-if="!isAuthenticated">
          <AuthRegisterQuit v-if="modalType == 'register-quit'" />
        </template>

        <template v-else>
          <Deposit v-if="modalType == 'deposit'" />
          <Withdraw v-if="modalType === 'withdraw'" />
          <UserWalletBonusRules v-if="modalType === 'bonus-rules'" />
          <UserWalletBonusWithdraw v-if="modalType === 'bonus-withdraw'" />
        </template>
      </div>
    </template>
  </BaseModal>
</template>

<style module lang="scss">
.modalIFrameContainer {
  @apply p-0 w-full h-full;

  .authFlowIframe {
    @apply w-full rounded-lg select-none;

    @media (max-width: 768px) {
      height: 100% !important;
    }
  }
}
</style>
